import { BBox, Vec2, clamp } from '@lakaran/geometry';

export const LOD_STEP = 1.25;

/**
 * Camera for a Y-up millimetre world rendered into a Y-down CSS pixel viewport.
 * Screen coordinates never leak into geometry code.
 */
export class Viewport {
  width = 1;
  height = 1;
  /** Screen pixels per millimetre. */
  scale = 1;
  center: Vec2 = Vec2.of(0, 0);
  minScale = 1e-4;
  maxScale = 20000;

  setSize(width: number, height: number): void {
    this.width = Math.max(1, width);
    this.height = Math.max(1, height);
  }

  worldToScreen(p: Vec2): Vec2 {
    return Vec2.of(
      (p.x - this.center.x) * this.scale + this.width / 2,
      this.height / 2 - (p.y - this.center.y) * this.scale,
    );
  }

  screenToWorld(p: Vec2): Vec2 {
    return Vec2.of(
      (p.x - this.width / 2) / this.scale + this.center.x,
      this.center.y - (p.y - this.height / 2) / this.scale,
    );
  }

  panByScreen(dx: number, dy: number): void {
    this.center = Vec2.of(this.center.x - dx / this.scale, this.center.y + dy / this.scale);
  }

  zoomAtScreen(screenPoint: Vec2, factor: number): void {
    const anchor = this.screenToWorld(screenPoint);
    this.scale = clamp(this.scale * factor, this.minScale, this.maxScale);
    this.center = Vec2.of(
      anchor.x - (screenPoint.x - this.width / 2) / this.scale,
      anchor.y + (screenPoint.y - this.height / 2) / this.scale,
    );
  }

  zoomToRect(box: BBox, paddingPx = 48): void {
    if (box.isEmpty) {
      this.scale = 1;
      this.center = Vec2.of(0, 0);
      return;
    }
    const availableW = Math.max(this.width - paddingPx * 2, 32);
    const availableH = Math.max(this.height - paddingPx * 2, 32);
    const w = Math.max(box.width, 1e-6);
    const h = Math.max(box.height, 1e-6);
    this.scale = clamp(Math.min(availableW / w, availableH / h), this.minScale, this.maxScale);
    this.center = box.center;
  }

  get bounds(): BBox {
    const halfW = this.width / 2 / this.scale;
    const halfH = this.height / 2 / this.scale;
    return new BBox(
      this.center.x - halfW,
      this.center.y - halfH,
      this.center.x + halfW,
      this.center.y + halfH,
    );
  }

  /** Converts a pixel distance to world millimetres. */
  pxToWorld(px: number): number {
    return px / this.scale;
  }

  /** Quantised zoom level; geometry is rebuilt when this changes. */
  lodBand(): number {
    return Math.round(Math.log(this.scale) / Math.log(LOD_STEP));
  }
}
